import { Injectable, signal } from '@angular/core';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  readonly isPwaInstalled = signal(false);
  readonly canInstallPwa = signal(false);

  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

  refreshInstallState(): void {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;

    this.isPwaInstalled.set(standalone);

    if (standalone) {
      this.canInstallPwa.set(false);
      this.deferredInstallPrompt = null;
    }
  }

  handleBeforeInstallPrompt(event: Event): void {
    const installEvent = event as BeforeInstallPromptEvent;

    event.preventDefault();

    if (this.isPwaInstalled()) {
      return;
    }

    this.deferredInstallPrompt = installEvent;
    this.canInstallPwa.set(true);
  }

  handleAppInstalled(): void {
    this.isPwaInstalled.set(true);
    this.canInstallPwa.set(false);
    this.deferredInstallPrompt = null;
  }

  async promptInstall(): Promise<void> {
    if (!this.deferredInstallPrompt || this.isPwaInstalled()) {
      return;
    }

    const installPrompt = this.deferredInstallPrompt;
    this.deferredInstallPrompt = null;
    this.canInstallPwa.set(false);

    await installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      this.isPwaInstalled.set(true);
    }
  }
}
